import * as React from "react";
import {AdminRegion, LoginContentAdminApi} from "../../api/LoginContentAdminApi";
import {IToastNotification} from "../../utils/planUserRemoval";
import {prominentButtonStyle, raisedButtonClassOnlyStyle} from "../common/buttonStyles";
import {useEffect, useState} from "react";
import {RaisedButton} from "../common/RaisedButton";

interface AdminLoginCountriesContentProps {
    activeRegion: AdminRegion;
    setToast: (toast: IToastNotification) => void;
    onCountriesSaved: (regionId: number) => void;
    regions: AdminRegion[];
}

export const LANGUAGE_VARIANT_TITLE = "Language variant";
export const LANGUAGE_VARIANT_ROUTING_TEXT = "This region inherits country routing from its parent region. Users whose " +
    "browser Accept-Language starts with <<<language_code>>> will see this variant. All other users in the same " +
    "country see the parent's English content.";
export const DEFAULT_REGION_TITLE = "Default does not have assigned countries";
export const DEFAULT_REGION_TEXT = "Default is the automatic fallback. Any user whose country is not assigned to " +
    "another region will see this content. For example, if someone logs in from Japan and Japan isn't assigned to any " +
    "region, they see Default.";
export const COUNTRIES_SECTION_TITLE = "Which countries see this region's content?";
export const COUNTRIES_SECTION_DESCRIPTION = "Any country mapped below will see this region's content. This lets you share the same content across multiple " +
    "countries without duplicating it.";
export const ASSIGNED_COUNTRIES_LABEL = "Assigned Countries";
export const NO_COUNTRIES_ASSIGNED_TEXT = "No countries assigned yet.";
export const ADD_COUNTRIES_LABEL = "Add Countries";
export const ADD_COUNTRIES_PLACEHOLDER = "Country code (e.g. US, GB, CA)";
export const ADD_COUNTRIES_TEXT = "Enter a 2-letter Cloudflare country code. A country can only belong to one region.";
export const INFOBOX_TEXT = "mapped to this region. Users from <<<audienceLabel>>> will see the <<<regionName>>> content on the login page. Any country not assigned to any region falls back to Default.";
export const SAVE_COUNTRIES_LABEL = "Save Countries";
export const COUNTRIES_SAVED = "Countries saved";
export const COUNTRIES_SAVE_ERROR = "Failed to save countries";

export const AdminLoginCountriesContent: React.FunctionComponent<AdminLoginCountriesContentProps> = (props) => {
    const [countryCodes, setCountryCodes] = useState<string[]>([]);
    const [newCountryCode, setNewCountryCode] = useState("");
    const [enableSaveButton, setEnableSaveButton] = useState(false);
    const countryCount = countryCodes.length;
    const countryLabel = countryCount === 1 ? "country" : "countries";
    const audienceLabel = countryCount === 1 ? "this country" : "any of these countries";

    useEffect(() => {
        setCountryCodes(props.activeRegion.countries);
        setEnableSaveButton(false);
        setNewCountryCode("");
    }, [props.activeRegion.id, props.activeRegion.countries]);

    const renderLanguageVariantContent = () => {
        const languageCode = props.activeRegion.languageCode ? props.activeRegion.languageCode.toUpperCase() : "";
        return <div className="admin-login-content__info-box">
            <i className="fa-solid fa-circle-info" />
            <div>
                <strong>{`${LANGUAGE_VARIANT_TITLE} — ${languageCode}`}</strong>
                <p>{LANGUAGE_VARIANT_ROUTING_TEXT.replace("<<<language_code>>>", languageCode)}</p>
            </div>
        </div>;
    };

    const renderDefaultRegionContent = () => {
        return <div className="admin-login-content__info-box">
            <i className="fa-solid fa-circle-info" />
            <div>
                <strong>{DEFAULT_REGION_TITLE}</strong>
                <p>{DEFAULT_REGION_TEXT}</p>
            </div>
        </div>;
    };

    const renderHeader = ()=>{
        return <div className="admin-login-content__countries-header">
            <h4>{COUNTRIES_SECTION_TITLE}</h4>
            <p>{COUNTRIES_SECTION_DESCRIPTION}</p>
        </div>;
    };

    const removeCountryCode = (code: string) => {
        setCountryCodes(countryCodes.filter(c => c !== code));
        setEnableSaveButton(true);
    };

    const renderAssignedCountries = () => {
       return   <div className="admin-login-content__form-group">
              <label className="admin-login-content__label" id="alc-assigned-countries">
                  {ASSIGNED_COUNTRIES_LABEL}
              </label>
              <div className="admin-login-content__country-chips" aria-labelledby="alc-assigned-countries">
                  {countryCodes.map(code => (
                      <span key={code} className="admin-login-content__country-chip">
                          {code}
                          <RaisedButton
                              type="button"
                              className="admin-login-content__country-chip-remove"
                              style={raisedButtonClassOnlyStyle}
                              primary={false}
                              onClick={() => removeCountryCode(code)}
                              aria-label={`Remove ${code}`}>
                                <i className="fa-solid fa-times" />
                            </RaisedButton>
                        </span>
                  ))}
                  {countryCodes.length === 0 && (
                      <span className="admin-login-content__empty-state">
                          {NO_COUNTRIES_ASSIGNED_TEXT}
                      </span>
                  )}
              </div>
          </div>;
    };

    const addCountryCode = () => {
        const code = newCountryCode.trim().toUpperCase();
        if (code.length < 2) return;
        if (countryCodes.includes(code)) {
            props.setToast({message: `${code} is already assigned`, type: "error"});
            return;
        }
        const conflictingRegion = props.regions
            .filter(r => r.id !== props.activeRegion.id)
            .find(r => r.countries.includes(code));
        if (conflictingRegion) {
            props.setToast({message: `${code} is already assigned to ${conflictingRegion.regionName}`, type: "error"});
            return;
        }
        setCountryCodes([...countryCodes, code]);
        setNewCountryCode("");
        setEnableSaveButton(true);
    };

    const renderAddCountries = () => {
        return <div className="admin-login-content__form-group">
                <label className="admin-login-content__label" htmlFor="alc-add-country">
                    {ADD_COUNTRIES_LABEL}
                </label>
                <div className="admin-login-content__add-country-row">
                    <input
                        id="alc-add-country"
                        type="text"
                        className="admin-login-content__input"
                        value={newCountryCode}
                        onChange={e => setNewCountryCode(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") addCountryCode(); }}
                        placeholder={ADD_COUNTRIES_PLACEHOLDER}
                        maxLength={2}
                    />
                    <RaisedButton
                        type="button"
                        className="admin-login-content__btn admin-login-content__btn--primary admin-login-content__btn--sm"
                        style={raisedButtonClassOnlyStyle}
                        primary={true}
                        onClick={addCountryCode}
                    >
                        <i className="fa-solid fa-plus" /> Add
                    </RaisedButton>
                </div>
                <span className="admin-login-content__form-hint">
                    {ADD_COUNTRIES_TEXT}
                </span>
            </div>;
    };

    const renderInfobox = () => {
        return <div className="admin-login-content__info-box admin-login-content__info-box--compact">
            <i className="fa-solid fa-circle-info" />
            <div>
                <strong>{countryCount} {countryLabel}</strong>
                {INFOBOX_TEXT
                    .replace("<<<audienceLabel>>>", audienceLabel)
                    .replace("<<<regionName>>>", props.activeRegion.regionName)}
            </div>
        </div>;
    };

    const saveCountriesHandler = async () => {
        LoginContentAdminApi.saveCountries(props.activeRegion.id, countryCodes)
        .then(() => {
            props.setToast({message: COUNTRIES_SAVED, type: "success"});
            props.onCountriesSaved(props.activeRegion.id);
            setEnableSaveButton(false);
        })
        .catch(() => {
            props.setToast({message: COUNTRIES_SAVE_ERROR, type: "error"});
        });
    };

    const renderSaveButton = () => {
        return <div className="admin-login-content__actions">
            <RaisedButton
                type="button"
                className="admin-login-content__btn admin-login-content__btn--primary"
                style={prominentButtonStyle}
                primary={true}
                onClick={() => void saveCountriesHandler()}
                disabled={!enableSaveButton}
            >
                <i className="fa-solid fa-check" /> {SAVE_COUNTRIES_LABEL}
            </RaisedButton>
        </div>;
    };

    const renderParentRegionContent = () => {
        return <div>
            {renderHeader()}
            {renderAssignedCountries()}
            {renderAddCountries()}
            {renderInfobox()}
            {renderSaveButton()}
        </div>;
    };

    return (
        <div className="admin-login-content__panel" data-testid="admin-login-content__countries-panel">
            {
            props.activeRegion.parentRegionId
                ? renderLanguageVariantContent()
                : props.activeRegion.isDefault
                    ? renderDefaultRegionContent()
                    : renderParentRegionContent()
            }
        </div>
    );
};
